import { pdf } from '@react-pdf/renderer';
import { CheckCircle2, XCircle, ListChecks, MessageSquare, Download } from 'lucide-react';

import { GuidePdf } from './guide-pdf';

import type { Guide } from '../data/accommodation-guides';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface GuideContentProps {
  guide: Guide;
}

export function GuideContent({ guide }: GuideContentProps) {
  const handleDownloadPdf = async () => {
    const blob = await pdf(<GuidePdf guide={guide} />).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Metaonia-Guide-${guide.title.replace(/\s+/g, '-')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">{guide.title}</h2>
          <p className="mt-2 text-muted-foreground">{guide.situation}</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
          <Download className="mr-2 h-4 w-4" />
          Download PDF
        </Button>
      </div>

      <Separator />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CheckCircle2 className="h-5 w-5 text-feedback-optimal" />
            What to Say
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {guide.whatToSay.map((phrase, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <span className="mt-0.5 text-feedback-optimal">•</span>
                <span className="italic">"{phrase}"</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <XCircle className="h-5 w-5 text-feedback-poor" />
            What NOT to Say
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {guide.whatNotToSay.map((phrase, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <span className="mt-0.5 text-feedback-poor">•</span>
                <span className="italic">"{phrase}"</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ListChecks className="h-5 w-5 text-primary" />
            Practical Steps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-inside list-decimal space-y-2 text-sm">
            {guide.practicalSteps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-5 w-5 text-primary" />
            Follow-up Template
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="italic text-sm">"{guide.followUpTemplate}"</p>
        </CardContent>
      </Card>
    </div>
  );
}
